"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMarkupStore } from "../store";
import type { FeedbackItem, FeedbackPriority, FeedbackType } from "../types";
import { captureScreenshot, getPageMetadata, generateId, cn, forcePageCursor } from "../utils";
import { getElementSelector } from "../utils/componentLocator";
import { getMarkupWindow, useMarkupWindow } from "../utils/deviceFrame";
import { CameraIcon, EditIcon, TrashIcon, PlusIcon } from "./Icons";
import { AnnotationOverlay } from "./AnnotationOverlay";

/** Things that travel alongside the ticket to the save, but aren't part of it. */
export interface FeedbackExtras {
    /** A newly recorded clip, uploaded to Storage as part of the save. */
    recording?: Blob | null;
}

/**
 * Pick a recording format this browser can actually produce. Safari records
 * MP4 only; asking it for WebM threw and was reported as "not supported".
 */
const pickRecordingMimeType = (): string | undefined => {
    if (typeof MediaRecorder === "undefined") return undefined;
    return [
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
        "video/mp4",
    ].find((type) => MediaRecorder.isTypeSupported?.(type));
};

/** Screen capture exists on desktop browsers only — not iOS or Android. */
const canCaptureScreen = (): boolean =>
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === "function";

interface FeedbackFormProps {
    /** Saves the ticket. Throw to keep the draft open and show the error. */
    onSubmit: (feedback: FeedbackItem, extras?: FeedbackExtras) => void | Promise<void>;
    onCancel: () => void;
    initialData?: FeedbackItem;
    getSessionEvents?: () => string | undefined;
    lastClickedElement?: Element | null;
    onDirtyChange?: (dirty: boolean) => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
    onSubmit,
    onCancel,
    initialData,
    getSessionEvents,
    lastClickedElement,
    onDirtyChange,
}) => {
    const {
        config,
        currentScreenshot,
        setCurrentScreenshot,
        annotations,
        setAnnotations,
        clearAnnotations,
        setIsCapturing,
        isCapturing,
        currentUser,
        setIsOpen,
        viewportMode,
    } = useMarkupStore();

    const [title, setTitle] = useState(initialData?.title || "");
    const [description, setDescription] = useState(
        initialData?.description || "",
    );
    const [priority, setPriority] = useState<FeedbackPriority>(
        initialData?.priority || "medium",
    );
    const [feedbackType, setFeedbackType] = useState<FeedbackType>(
        initialData?.type || "general",
    );
    const [currentCopy, setCurrentCopy] = useState(
        initialData?.currentCopy || "",
    );
    const [newCopy, setNewCopy] = useState(initialData?.newCopy || "");
    const [designMockup, setDesignMockup] = useState<string | undefined>(
        initialData?.designMockup,
    );
    const [selectedTags, setSelectedTags] = useState<string[]>(
        initialData?.tags || [],
    );
    const [isAnnotating, setIsAnnotating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [annotatedScreenshot, setAnnotatedScreenshot] = useState<
        string | null
    >(initialData?.screenshot || null);

    // Screen recording states
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessingVideo, setIsProcessingVideo] = useState(false);
    const [recordedVideo, setRecordedVideo] = useState<string | undefined>(
        initialData?.screenRecording,
    );
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
    const [recordedBlobSizeMB, setRecordedBlobSizeMB] = useState<number>(0);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
        null,
    );
    const [recordingStream, setRecordingStream] = useState<MediaStream | null>(
        null,
    );

    const [pinError, setPinError] = useState(false);

    // The page being marked up — the device preview while one is open. Pickers
    // listen on its document, because clicks inside an iframe never reach
    // ours. A change (the preview navigated) re-arms any active picker there.
    const markupWindow = useMarkupWindow();

    // Pin placement states
    const [isPlacingPin, setIsPlacingPin] = useState(false);
    const [pinPosition, setPinPositionState] = useState<{
        x: number;
        y: number;
        pageX: number;
        pageY: number;
        elementSelector?: string;
        elementOffsetX?: number;
        elementOffsetY?: number;
    } | null>(
        // Carry the element anchoring over too. Editing used to keep only the
        // coordinates, so saving an edit detached the pin from its element
        // and it drifted on the next layout change.
        initialData?.pinLocation
            ? {
                  x: initialData.pinLocation.x,
                  y: initialData.pinLocation.y,
                  pageX: initialData.pinLocation.pageX,
                  pageY: initialData.pinLocation.pageY,
                  elementSelector: initialData.pinLocation.elementSelector,
                  elementOffsetX: initialData.pinLocation.elementOffsetX,
                  elementOffsetY: initialData.pinLocation.elementOffsetY,
              }
            : null,
    );
    // Whether the pin has been placed in this session; an untouched pin keeps
    // its saved location exactly (page size, percentages and all).
    const [pinMoved, setPinMoved] = useState(false);
    const setPinPosition = useCallback(
        (next: NonNullable<typeof pinPosition>) => {
            setPinPositionState(next);
            setPinMoved(true);
        },
        [],
    );
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [captureError, setCaptureError] = useState<string | null>(null);
    // Set when the user cancels a recording, so the recorder's stop handler
    // (which fires either way) discards the clip instead of keeping it.
    const discardRecordingRef = useRef(false);

    // Text change (copy amendment) states
    const [isSelectingText, setIsSelectingText] = useState(false);
    // Ref to the DOM element whose text the client clicked on
    const targetTextElementRef = useRef<HTMLElement | null>(null);
    // Store original innerHTML so we can restore perfectly (including child elements)
    const originalInnerHTMLRef = useRef<string>("");
    const previewActiveRef = useRef(false);

    // Restore the target element to its original HTML — called on submit, cancel, and unmount
    const restoreTargetElement = useCallback(() => {
        if (targetTextElementRef.current && previewActiveRef.current) {
            targetTextElementRef.current.innerHTML = originalInnerHTMLRef.current;
            targetTextElementRef.current.style.removeProperty("outline");
            targetTextElementRef.current.style.removeProperty("outline-offset");
            targetTextElementRef.current.style.removeProperty("border-radius");
            targetTextElementRef.current.style.removeProperty("background-color");
            previewActiveRef.current = false;
        }
    }, []);

    // Restore on unmount so the page is never left modified
    useEffect(() => () => restoreTargetElement(), [restoreTargetElement]);

    // Live preview: update the target element's text as the client types the new copy
    useEffect(() => {
        if (!targetTextElementRef.current || feedbackType !== "copy-amendment") return;
        if (newCopy.trim()) {
            targetTextElementRef.current.textContent = newCopy;
            targetTextElementRef.current.style.outline = "2px solid #22c55e";
            targetTextElementRef.current.style.outlineOffset = "3px";
            targetTextElementRef.current.style.borderRadius = "3px";
            targetTextElementRef.current.style.backgroundColor = "rgba(34,197,94,0.08)";
            previewActiveRef.current = true;
        } else {
            // When field is cleared, restore original so the diff is visible
            targetTextElementRef.current.innerHTML = originalInnerHTMLRef.current;
            targetTextElementRef.current.style.outline = "2px dashed #f59e0b";
            targetTextElementRef.current.style.outlineOffset = "3px";
            targetTextElementRef.current.style.borderRadius = "3px";
            targetTextElementRef.current.style.removeProperty("background-color");
            previewActiveRef.current = false;
        }
    }, [newCopy, feedbackType]);

    // Image change states
    const [isSelectingImage, setIsSelectingImage] = useState(false);

    // Tell the pins to step aside while any picker waits for a click.
    const setIsPicking = useMarkupStore((st) => st.setIsPicking);
    useEffect(() => {
        const picking = isPlacingPin || isSelectingText || isSelectingImage;
        setIsPicking(picking);
        return () => setIsPicking(false);
    }, [isPlacingPin, isSelectingText, isSelectingImage, setIsPicking]);

    const [originalImageSrc, setOriginalImageSrc] = useState(
        initialData?.originalImageSrc || "",
    );
    const [originalImageBackground, setOriginalImageBackground] = useState(
        initialData?.originalImageBackground || "",
    );
    const [replacementImageUrl, setReplacementImageUrl] = useState(
        initialData?.replacementImageUrl || "",
    );

    // Notify parent when form becomes dirty
    useEffect(() => {
        if (!onDirtyChange || initialData) return;
        const dirty = !!(title || description || newCopy || currentCopy || pinPosition || annotatedScreenshot || recordedVideo);
        onDirtyChange(dirty);
    }, [title, description, newCopy, currentCopy, pinPosition, annotatedScreenshot, recordedVideo, onDirtyChange, initialData]);

    // Load initial annotations into store if editing
    useEffect(() => {
        if (initialData?.annotations) {
            setAnnotations(initialData.annotations);
        }
    }, [initialData, setAnnotations]);

    // Handle pin placement mode
    useEffect(() => {
        if (!isPlacingPin || !markupWindow) return;
        const view = markupWindow;
        const doc = view.document;

        // Close widget during pin placement
        setIsOpen(false);

        const handlePinClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // Capture element info so pins can track the element when the layout changes
            let elementSelector: string | undefined;
            let elementOffsetX: number | undefined;
            let elementOffsetY: number | undefined;
            const target = e.target as Element | null;
            if (
                target &&
                target !== doc.documentElement &&
                target !== doc.body
            ) {
                try {
                    elementSelector = getElementSelector(target);
                    const rect = target.getBoundingClientRect();
                    elementOffsetX = e.pageX - (rect.left + view.scrollX);
                    elementOffsetY = e.pageY - (rect.top + view.scrollY);
                } catch (_) {
                    // non-critical – fall back to percentage-based positioning
                }
            }

            setPinPosition({
                x: e.clientX,
                y: e.clientY,
                pageX: e.pageX,
                pageY: e.pageY,
                elementSelector,
                elementOffsetX,
                elementOffsetY,
            });
            setPinError(false);
            setIsPlacingPin(false);
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            // Claimed, so the device preview doesn't read it as "exit".
            e.preventDefault();
            setIsPlacingPin(false);
        };

        doc.addEventListener("click", handlePinClick, true);
        doc.addEventListener("keydown", handleEscape, true);
        if (doc !== document) document.addEventListener("keydown", handleEscape, true);
        const restoreCursor = forcePageCursor(doc, "crosshair");

        return () => {
            doc.removeEventListener("click", handlePinClick, true);
            doc.removeEventListener("keydown", handleEscape, true);
            document.removeEventListener("keydown", handleEscape, true);
            restoreCursor();
            setIsOpen(true);
        };
    }, [isPlacingPin, setIsOpen, markupWindow, setPinPosition]);

    // Handle image selection mode for image-change type
    useEffect(() => {
        if (!isSelectingImage || !markupWindow) return;
        const view = markupWindow;
        const doc = view.document;

        // Close widget during image selection
        setIsOpen(false);

        const handleImageClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const target = e.target as HTMLElement;

            // Check for img tag
            if (target.tagName === "IMG") {
                const img = target as HTMLImageElement;
                setOriginalImageSrc(img.src);
                setOriginalImageBackground("");

                // Place pin at image location
                let elSel: string | undefined;
                let elOffX: number | undefined;
                let elOffY: number | undefined;
                try {
                    elSel = getElementSelector(target);
                    const rect = target.getBoundingClientRect();
                    elOffX = e.pageX - (rect.left + view.scrollX);
                    elOffY = e.pageY - (rect.top + view.scrollY);
                } catch (_) {
                    /* non-critical */
                }
                setPinPosition({
                    x: e.clientX,
                    y: e.clientY,
                    pageX: e.pageX,
                    pageY: e.pageY,
                    elementSelector: elSel,
                    elementOffsetX: elOffX,
                    elementOffsetY: elOffY,
                });
                setIsSelectingImage(false);
                return;
            }

            // Check for background-image
            const bgImage = view.getComputedStyle(target).backgroundImage;
            if (bgImage && bgImage !== "none") {
                // Extract URL from url("...") or url('...')
                const urlMatch = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
                if (urlMatch) {
                    setOriginalImageBackground(urlMatch[1]);
                    setOriginalImageSrc("");

                    // Place pin at element location
                    let elSel: string | undefined;
                    let elOffX: number | undefined;
                    let elOffY: number | undefined;
                    try {
                        elSel = getElementSelector(target);
                        const rect = target.getBoundingClientRect();
                        elOffX = e.pageX - (rect.left + view.scrollX);
                        elOffY = e.pageY - (rect.top + view.scrollY);
                    } catch (_) {
                        /* non-critical */
                    }
                    setPinPosition({
                        x: e.clientX,
                        y: e.clientY,
                        pageX: e.pageX,
                        pageY: e.pageY,
                        elementSelector: elSel,
                        elementOffsetX: elOffX,
                        elementOffsetY: elOffY,
                    });
                    setIsSelectingImage(false);
                }
            }
        };

        // Add hover effect to show which element would be selected
        const handleImageHover = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const isImage = target.tagName === "IMG";
            const hasBgImage =
                view.getComputedStyle(target).backgroundImage !== "none";

            if (isImage || hasBgImage) {
                target.style.outline = "3px solid #3b82f6";
                target.style.outlineOffset = "2px";
            }
        };

        const handleImageHoverOut = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            target.style.outline = "";
            target.style.outlineOffset = "";
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            // Claimed, so the device preview doesn't read it as "exit".
            e.preventDefault();
            setIsSelectingImage(false);
        };

        doc.addEventListener("click", handleImageClick, true);
        doc.addEventListener("mouseover", handleImageHover, true);
        doc.addEventListener("mouseout", handleImageHoverOut, true);
        doc.addEventListener("keydown", handleEscape, true);
        if (doc !== document) document.addEventListener("keydown", handleEscape, true);
        const restoreCursor = forcePageCursor(doc, "crosshair");

        return () => {
            doc.removeEventListener("click", handleImageClick, true);
            doc.removeEventListener("mouseover", handleImageHover, true);
            doc.removeEventListener("mouseout", handleImageHoverOut, true);
            doc.removeEventListener("keydown", handleEscape, true);
            document.removeEventListener("keydown", handleEscape, true);
            restoreCursor();
            setIsOpen(true);
        };
    }, [isSelectingImage, setIsOpen, markupWindow, setPinPosition]);

    // Text selection mode — client clicks on a text element to target it
    useEffect(() => {
        if (!isSelectingText || !markupWindow) return;
        const view = markupWindow;
        const doc = view.document;

        // Restore any existing preview before entering selection again
        restoreTargetElement();
        targetTextElementRef.current = null;
        setIsOpen(false);

        // Only target elements whose visible content is mostly text (no block children)
        const isTargetable = (el: HTMLElement): boolean => {
            if (el.closest("[data-markup-widget-root]")) return false;
            const text = el.textContent?.trim() ?? "";
            if (text.length < 2) return false;
            // Reject elements whose children are block-level (headers, divs with layout)
            for (const child of Array.from(el.children)) {
                const display = view.getComputedStyle(child as HTMLElement).display;
                if (display === "block" || display === "flex" || display === "grid" || display === "table") return false;
            }
            return true;
        };

        let lastHovered: HTMLElement | null = null;

        const handleMouseOver = (e: MouseEvent) => {
            if (lastHovered) {
                lastHovered.style.removeProperty("outline");
                lastHovered.style.removeProperty("outline-offset");
                lastHovered.style.removeProperty("cursor");
                lastHovered = null;
            }
            const el = e.target as HTMLElement;
            if (isTargetable(el)) {
                el.style.outline = "2px solid #d32df4";
                el.style.outlineOffset = "3px";
                // Inline !important, to beat the page-wide text cursor.
                el.style.setProperty("cursor", "pointer", "important");
                lastHovered = el;
            }
        };

        const handleMouseOut = (e: MouseEvent) => {
            const el = e.target as HTMLElement;
            if (el === lastHovered) {
                el.style.removeProperty("outline");
                el.style.removeProperty("outline-offset");
                el.style.removeProperty("cursor");
                lastHovered = null;
            }
        };

        const handleClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const el = e.target as HTMLElement;
            if (!isTargetable(el)) return;

            // Capture element + its original HTML
            targetTextElementRef.current = el;
            originalInnerHTMLRef.current = el.innerHTML;
            const text = el.textContent?.trim() ?? "";

            setCurrentCopy(text);

            // Auto-place the pin at the centre of the element so clients don't have to
            const rect = el.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            try {
                const sel = getElementSelector(el);
                setPinPosition({
                    x: cx,
                    y: cy,
                    pageX: cx + view.scrollX,
                    pageY: cy + view.scrollY,
                    elementSelector: sel,
                    elementOffsetX: rect.width / 2,
                    elementOffsetY: rect.height / 2,
                });
            } catch {
                setPinPosition({
                    x: cx, y: cy,
                    pageX: cx + view.scrollX,
                    pageY: cy + view.scrollY,
                });
            }
            setPinError(false);

            // Mark element with amber outline so client can see what's targeted
            el.style.outline = "2px dashed #f59e0b";
            el.style.outlineOffset = "3px";
            el.style.borderRadius = "3px";

            setIsSelectingText(false);
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            // Claimed, so the device preview doesn't read it as "exit".
            e.preventDefault();
            setIsSelectingText(false);
        };

        doc.addEventListener("mouseover", handleMouseOver, true);
        doc.addEventListener("mouseout", handleMouseOut, true);
        doc.addEventListener("click", handleClick, true);
        doc.addEventListener("keydown", handleEscape, true);
        if (doc !== document) document.addEventListener("keydown", handleEscape, true);
        const restoreCursor = forcePageCursor(doc, "text");

        return () => {
            doc.removeEventListener("mouseover", handleMouseOver, true);
            doc.removeEventListener("mouseout", handleMouseOut, true);
            doc.removeEventListener("click", handleClick, true);
            doc.removeEventListener("keydown", handleEscape, true);
            document.removeEventListener("keydown", handleEscape, true);
            restoreCursor();
            if (lastHovered) {
                lastHovered.style.removeProperty("outline");
                lastHovered.style.removeProperty("outline-offset");
                lastHovered.style.removeProperty("cursor");
            }
            setIsOpen(true);
        };
    }, [isSelectingText, setIsOpen, restoreTargetElement, markupWindow, setPinPosition]);

    const handleCaptureScreenshot = async () => {
        setCaptureError(null);
        if (!canCaptureScreen()) {
            setCaptureError(
                "Screenshots need a desktop browser. On a phone or tablet, use your device’s screenshot and upload it as a mockup below.",
            );
            return;
        }
        try {
            setIsCapturing(true);
            const screenshot = await captureScreenshot();
            setCurrentScreenshot(screenshot);
            setIsAnnotating(true);
        } catch (error: any) {
            // Dismissing the browser's share picker isn't an error worth
            // shouting about; anything else is.
            if (error?.name !== "NotAllowedError" && error?.name !== "AbortError") {
                setCaptureError("Couldn’t take a screenshot. Please try again.");
            }
        } finally {
            setIsCapturing(false);
        }
    };

    const handleStartRecording = async () => {
        setCaptureError(null);
        if (!canCaptureScreen() || typeof MediaRecorder === "undefined") {
            setCaptureError(
                "Screen recording needs a desktop browser (Chrome, Edge, Firefox or Safari).",
            );
            return;
        }
        let stream: MediaStream;
        try {
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: false,
            });
        } catch (error: any) {
            if (error?.name !== "NotAllowedError" && error?.name !== "AbortError") {
                setCaptureError("Couldn’t start recording. Please try again.");
            }
            return;
        }

        const mimeType = pickRecordingMimeType();
        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        } catch (_) {
            stream.getTracks().forEach((track) => track.stop());
            setCaptureError("This browser can’t record the screen.");
            return;
        }

        const chunks: Blob[] = [];
        discardRecordingRef.current = false;
        setRecordingStream(stream);

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = () => {
            stream.getTracks().forEach((track) => track.stop());
            setRecordingStream(null);
            setMediaRecorder(null);
            setIsRecording(false);
            setIsProcessingVideo(false);

            if (discardRecordingRef.current || chunks.length === 0) return;

            const blob = new Blob(chunks, {
                type: recorder.mimeType || mimeType || "video/webm",
            });
            setRecordedBlob(blob);
            setRecordedBlobSizeMB(blob.size / 1048576);
            setRecordedVideo(URL.createObjectURL(blob));
        };

        // Stopped from the browser's own "Stop sharing" bar: finish the
        // recording as if Stop had been pressed, instead of leaving the form
        // stuck in its recording state.
        stream.getVideoTracks()[0]?.addEventListener("ended", () => {
            if (recorder.state !== "inactive") {
                setIsProcessingVideo(true);
                recorder.stop();
            }
        });

        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
    };

    const handleStopRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== "inactive") {
            setIsProcessingVideo(true);
            mediaRecorder.stop();
        }
    };

    const handleCancelRecording = () => {
        // stop() still fires onstop; the flag makes it throw the clip away.
        // Previously "Cancel" kept the recording.
        discardRecordingRef.current = true;
        if (mediaRecorder && mediaRecorder.state !== "inactive") {
            mediaRecorder.stop();
        }
        recordingStream?.getTracks().forEach((track) => track.stop());
        setRecordingStream(null);
        setIsRecording(false);
        setIsProcessingVideo(false);
        setMediaRecorder(null);
    };

    const handleDeleteRecording = () => {
        // Revoke blob URL to free memory
        if (recordedVideo && recordedVideo.startsWith("blob:")) {
            URL.revokeObjectURL(recordedVideo);
        }
        setRecordedVideo(undefined);
        setRecordedBlob(null);
        if (recordingStream) {
            recordingStream.getTracks().forEach((track) => track.stop());
            setRecordingStream(null);
        }
    };

    const handleAnnotationComplete = (
        newAnnotations: any[],
        screenshot: string,
    ) => {
        setAnnotatedScreenshot(screenshot);
        setIsAnnotating(false);
    };

    const handleAnnotationCancel = () => {
        setIsAnnotating(false);
        setCurrentScreenshot(null);
        clearAnnotations();
    };

    const handleRemoveScreenshot = () => {
        setCurrentScreenshot(null);
        setAnnotatedScreenshot(null);
        clearAnnotations();
    };

    const handleEditScreenshot = () => {
        if (currentScreenshot) {
            setIsAnnotating(true);
        }
    };

    const toggleTag = (tag: string) => {
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
        );
    };

    const priorityColors: Record<string, string> = {
        low: "bg-matter-ink/5 text-gray-700",
        medium: "bg-matter-ink/10 text-gray-800",
        high: "bg-matter-accent/15 text-gray-800",
        critical: "bg-matter-accent text-white",
    };

    const handleMockupUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setDesignMockup(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // What each type needs before it can be sent. One rule, used by both the
    // button and the submit handler — they used to disagree, so an image
    // change couldn't be sent without a title the handler didn't require.
    const canSubmit =
        feedbackType === "copy-amendment"
            ? !!newCopy.trim()
            : feedbackType === "image-change"
              ? !!replacementImageUrl.trim()
              : !!title.trim();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit || isSubmitting) return;
        if (!pinPosition && !initialData?.pinLocation) {
            setPinError(true);
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        // Measured on the page the pin was placed on — the preview's, if open.
        const view = getMarkupWindow();
        const pageWidth = view.document.documentElement.scrollWidth;
        const pageHeight = view.document.documentElement.scrollHeight;
        const rawMetadata =
            initialData?.pageMetadata ||
            getPageMetadata(lastClickedElement || undefined);
        // When in responsive/viewport mode, stamp the simulated device type so
        // reviewers know this feedback was captured at mobile/tablet dimensions.
        const pageMetadata = viewportMode
            ? { ...rawMetadata, deviceType: viewportMode.deviceType }
            : rawMetadata;

        const feedback: FeedbackItem = {
            id: initialData?.id || generateId(),
            type: feedbackType,
            title:
                title.trim() ||
                (feedbackType === "copy-amendment"
                    ? "Copy Amendment"
                    : feedbackType === "image-change"
                      ? "Image Change"
                      : ""),
            description: description.trim(),
            currentCopy:
                feedbackType === "copy-amendment"
                    ? currentCopy.trim()
                    : undefined,
            newCopy:
                feedbackType === "copy-amendment" ? newCopy.trim() : undefined,
            screenshot: annotatedScreenshot || currentScreenshot || undefined,
            annotations: annotations,
            status: initialData?.status || "open",
            priority,
            pageMetadata,
            createdAt: initialData?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: initialData?.createdBy ||
                currentUser || {
                    id: config.user?.id || "anonymous",
                    name: config.user?.name || "Anonymous",
                    email: config.user?.email || "",
                    role: config.user?.role || "default",
                },
            comments: initialData?.comments || [],
            tags: selectedTags,
            sessionEvents: getSessionEvents ? getSessionEvents() : undefined,
            designMockup,
            // A fresh recording goes up with the save (see FeedbackExtras);
            // an existing one keeps its stored URL.
            screenRecording: recordedBlob ? undefined : recordedVideo,
            pinLocation: !pinMoved && initialData?.pinLocation
                ? initialData.pinLocation
                : pinPosition
                ? {
                      x: pinPosition.x,
                      y: pinPosition.y,
                      pageX: pinPosition.pageX,
                      pageY: pinPosition.pageY,
                      viewportWidth: view.innerWidth,
                      viewportHeight: view.innerHeight,
                      pageWidth,
                      pageHeight,
                      scrollX: view.scrollX,
                      scrollY: view.scrollY,
                      percentX: pageWidth
                          ? (pinPosition.pageX / pageWidth) * 100
                          : undefined,
                      percentY: pageHeight
                          ? (pinPosition.pageY / pageHeight) * 100
                          : undefined,
                      elementSelector: pinPosition.elementSelector,
                      elementOffsetX: pinPosition.elementOffsetX,
                      elementOffsetY: pinPosition.elementOffsetY,
                  }
                : undefined,
            // Image change data
            originalImageSrc:
                feedbackType === "image-change" ? originalImageSrc : undefined,
            originalImageBackground:
                feedbackType === "image-change"
                    ? originalImageBackground
                    : undefined,
            replacementImageUrl:
                feedbackType === "image-change"
                    ? replacementImageUrl
                    : undefined,
        };

        try {
            if (config.onSubmit) {
                await config.onSubmit(feedback);
            }
            await onSubmit(feedback, { recording: recordedBlob });
            restoreTargetElement();
            onDirtyChange?.(false);

            setTitle("");
            setDescription("");
            setPriority("medium");
            setSelectedTags([]);
            setCurrentScreenshot(null);
            setAnnotatedScreenshot(null);
            clearAnnotations();
        } catch (error: any) {
            // Keep everything the user entered so they can retry.
            console.error("[markup] Failed to submit feedback");
            setSubmitError(
                error?.code === "permission-denied"
                    ? "You don’t have permission to save this. Try signing out and in again."
                    : error?.message?.includes("storage")
                      ? "Couldn’t upload the attachments. Check your connection and try again."
                      : "Couldn’t save your feedback. Check your connection and try again — nothing you entered has been lost.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isAnnotating && currentScreenshot) {
        return (
            <AnnotationOverlay
                screenshot={currentScreenshot}
                onComplete={handleAnnotationComplete}
                onCancel={handleAnnotationCancel}
            />
        );
    }

    // Show pin placement overlay
    if (isPlacingPin) {
        return (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[999999] pointer-events-none">
                <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-4 text-center border-2 border-orange-500">
                    <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-orange-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                    </div>
                    <div className="text-lg! font-bold text-gray-900 mb-2">
                        Click to Place Pin
                    </div>
                    <p className="text-gray-600 text-sm! mb-3">
                        Click anywhere on the page to mark the location
                    </p>
                    <button
                        type="button"
                        onClick={() => setIsPlacingPin(false)}
                        className="px-4 py-2 text-sm! font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors pointer-events-auto"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    // Show text selection overlay
    if (isSelectingText) {
        return (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[999999] pointer-events-none">
                <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm mx-4 text-center border-2 border-matter-accent/60">
                    <div className="w-12 h-12 mx-auto mb-3 bg-matter-accent/10 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-matter-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                        </svg>
                    </div>
                    <div className="text-base! font-bold text-gray-900 mb-1">Click on the text to change</div>
                    <p className="text-gray-500 text-xs! mb-4 m-0">Hover over any text to highlight it, then click to select it</p>
                    <button
                        type="button"
                        onClick={() => setIsSelectingText(false)}
                        className="px-4 py-2 text-sm! font-medium text-white bg-matter-ink hover:bg-matter-ink/80 rounded-lg transition-colors pointer-events-auto"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    // Show image selection overlay
    if (isSelectingImage) {
        return (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[999999] pointer-events-none">
                <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-4 text-center border-2 border-blue-500">
                    <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <div className="text-lg! font-bold text-gray-900 mb-2">
                        Click on an Image
                    </div>
                    <p className="text-gray-600 text-sm! mb-3">
                        Click on any image or element with a background image
                    </p>
                    <button
                        type="button"
                        onClick={() => setIsSelectingImage(false)}
                        className="px-4 py-2 text-sm! font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors pointer-events-auto"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            {/* Viewport mode context banner */}
            {viewportMode && (
                <div className="flex items-center gap-2 mb-5 px-3 py-2.5 rounded-xl bg-matter-ink/5 border border-matter-ink/10">
                    <svg className="w-4 h-4 text-matter-accent shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs! font-medium text-gray-700">
                        <span className="font-semibold">{viewportMode.name}</span>
                        <span className="text-gray-500 ml-1">· {viewportMode.width}×{viewportMode.height} · This feedback will be tagged as {viewportMode.deviceType}</span>
                    </span>
                </div>
            )}

            {/* Feedback Type */}
            <div className="mb-5">
                <label className="block text-sm! font-semibold mb-2 text-gray-800">
                    Type
                </label>
                <div className="flex gap-2 flex-wrap">
                    {[
                        { id: "general", label: "General" },
                        { id: "copy-amendment", label: "Text Change" },
                        { id: "image-change", label: "Image Change" },
                    ].map((type) => (
                        <button type="button"
                            key={type.id}
                            onClick={() => {
                                setFeedbackType(type.id as FeedbackType);
                                // When switching to Text Change with no target yet, immediately
                                // enter selection mode so the client clicks on the text to change
                                if (type.id === "copy-amendment" && !targetTextElementRef.current) {
                                    setIsSelectingText(true);
                                }
                                // Switching away from copy-amendment: restore the page
                                if (type.id !== "copy-amendment") {
                                    restoreTargetElement();
                                    targetTextElementRef.current = null;
                                    setCurrentCopy("");
                                    setNewCopy("");
                                }
                            }}
                            className={cn(
                                "flex-1 py-2 px-3 rounded-xl text-xs! font-medium cursor-pointer transition-all text-center",
                                feedbackType === type.id
                                    ? "bg-matter-ink text-matter-cream shadow-md shadow-matter-ink/30"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm",
                            )}
                        >
                            {type.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Image Change Fields */}
            {feedbackType === "image-change" && (
                <>
                    <div className="mb-5">
                        <label className="block text-sm! font-semibold mb-2 text-gray-800">
                            Select Image
                        </label>
                        <button
                            type="button"
                            onClick={() => setIsSelectingImage(true)}
                            className="w-full px-4 py-3 bg-foreground border  rounded-xl text-sm! font-medium text-background  transition-all flex items-center justify-center gap-2"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                                />
                            </svg>
                            {originalImageSrc || originalImageBackground
                                ? "Change Image"
                                : "Click to Select Image"}
                        </button>
                        {(originalImageSrc || originalImageBackground) && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-xs! font-semibold text-gray-700 mb-2">
                                    Current Image:
                                </p>
                                {originalImageSrc ? (
                                    <>
                                        <img
                                            src={originalImageSrc}
                                            alt="Selected"
                                            className="w-full h-32 object-cover rounded-lg mb-2"
                                        />
                                        <p className="text-[10px] text-gray-500 font-mono break-all">
                                            {originalImageSrc}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-[10px] text-gray-500 font-mono break-all">
                                        Background: {originalImageBackground}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mb-5">
                        <label className="block text-sm! font-semibold mb-2 text-gray-800">
                            Replacement Image URL *
                        </label>
                        <input
                            type="url"
                            placeholder="https://example.com/new-image.jpg"
                            value={replacementImageUrl}
                            onChange={(e) =>
                                setReplacementImageUrl(e.target.value)
                            }
                            required={feedbackType === "image-change"}
                            className="w-full px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm! focus:outline-none focus:bg-white focus:border-green-500 focus:shadow-lg focus:shadow-green-500/10 transition-all"
                        />
                        {replacementImageUrl && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-xs! font-semibold text-gray-700 mb-2">
                                    Preview:
                                </p>
                                <img
                                    src={replacementImageUrl}
                                    alt="Replacement Preview"
                                    className="w-full h-32 object-cover rounded-lg"
                                    onError={(e) => {
                                        (
                                            e.target as HTMLImageElement
                                        ).style.display = "none";
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Text Change Fields */}
            {feedbackType === "copy-amendment" && (
                <div className="mb-5">
                    {currentCopy ? (
                        <>
                            {/* Visual diff card */}
                            <div className="mb-4 rounded-xl border border-gray-200 overflow-hidden">
                                {/* Header */}
                                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                                    <span className="text-xs! font-semibold text-gray-500 uppercase tracking-wide">Live Preview</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            restoreTargetElement();
                                            targetTextElementRef.current = null;
                                            setCurrentCopy("");
                                            setNewCopy("");
                                            setIsSelectingText(true);
                                        }}
                                        className="text-xs! text-matter-accent hover:text-matter-ink font-medium bg-transparent border-none cursor-pointer transition-colors"
                                    >
                                        Change selection
                                    </button>
                                </div>
                                {/* Diff body */}
                                <div className="p-3 space-y-2">
                                    {/* Original */}
                                    <div className="flex gap-2 items-start">
                                        <span className="mt-0.5 w-4 h-4 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                                            <svg className="w-2.5 h-2.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M20 12H4" />
                                            </svg>
                                        </span>
                                        <p className="text-sm! text-gray-500 line-through decoration-red-400 leading-snug m-0">{currentCopy}</p>
                                    </div>
                                    {/* Replacement (shown once client starts typing) */}
                                    {newCopy.trim() && (
                                        <div className="flex gap-2 items-start">
                                            <span className="mt-0.5 w-4 h-4 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                                <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" />
                                                </svg>
                                            </span>
                                            <p className="text-sm! text-green-700 font-medium leading-snug m-0">{newCopy}</p>
                                        </div>
                                    )}
                                </div>
                                {/* Live preview hint */}
                                {newCopy.trim() && (
                                    <div className="px-3 py-2 bg-green-50 border-t border-green-100 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
                                        <span className="text-[10px] text-green-700 font-medium">Live preview active on page</span>
                                    </div>
                                )}
                            </div>

                            {/* New copy textarea */}
                            <label className="block text-sm! font-semibold mb-2 text-gray-800">
                                Replacement text *
                            </label>
                            <textarea
                                autoFocus
                                placeholder="Type the replacement text… the page updates live"
                                value={newCopy}
                                onChange={(e) => setNewCopy(e.target.value)}
                                required={feedbackType === "copy-amendment"}
                                className="w-full px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm! min-h-[80px] resize-y focus:outline-none focus:bg-white focus:border-green-400 focus:shadow-lg focus:shadow-green-500/10 transition-all"
                            />
                        </>
                    ) : (
                        /* No target selected yet — prompt to click on page */
                        <button type="button"
                            onClick={() => setIsSelectingText(true)}
                            className="p-5 bg-matter-accent/5 border-2 border-dashed border-matter-accent/40 rounded-xl cursor-pointer flex flex-col items-center justify-center gap-2 text-center hover:border-matter-accent/60 hover:bg-matter-accent/10 transition-all"
                        >
                            <div className="w-10 h-10 rounded-full bg-matter-accent/10 flex items-center justify-center">
                                <svg className="w-5 h-5 text-matter-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm! font-semibold text-matter-ink m-0">Click to select text on page</p>
                                <p className="text-xs! text-matter-accent mt-0.5 m-0">The widget will close — hover over text to highlight it, then click</p>
                            </div>
                        </button>
                    )}
                </div>
            )}

            {/* Title */}
            {feedbackType !== "copy-amendment" && (
                <div className="mb-5">
                    <label
                        htmlFor="markup-feedback-title"
                        className="block text-sm! font-semibold mb-2 text-gray-800"
                    >
                        Title{feedbackType === "general" ? " *" : ""}
                    </label>
                    <input
                        id="markup-feedback-title"
                        type="text"
                        placeholder="Brief description of the issue"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        // Only a general ticket needs a title; the others are
                        // named after their change.
                        required={feedbackType === "general"}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm! focus:outline-none focus:bg-white focus:border-matter-ink/40 focus:shadow-lg focus:shadow-matter-ink/10 transition-all"
                    />
                </div>
            )}

            {/* Description */}
            {feedbackType !== "copy-amendment" && (
                <div className="mb-5">
                    <label
                        htmlFor="markup-feedback-description"
                        className="block text-sm! font-semibold mb-2 text-gray-800"
                    >
                        Description
                    </label>
                    {/* Optional. It was marked `required` without saying so;
                        harmless while the submit button was a <div> that
                        skipped browser validation, but a real submit button
                        refused to send any ticket without one. */}
                    <textarea
                        id="markup-feedback-description"
                        placeholder="Provide more details about the issue..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm! focus:outline-none focus:bg-white focus:border-matter-ink/40 focus:shadow-lg focus:shadow-matter-ink/10 transition-all min-h-[100px] resize-y"
                    />
                </div>
            )}

            {/* Priority */}
            <div className="mb-5">
                <label className="block text-sm! font-semibold mb-2 text-gray-800">
                    Priority
                </label>
                <div className="flex gap-2">
                    {(Object.keys(priorityColors) as FeedbackPriority[]).map(
                        (p) => (
                            <button type="button"
                                key={p}
                                onClick={() => setPriority(p)}
                                className={cn(
                                    "flex-1 py-2 px-2 rounded-xl text-xs! font-medium cursor-pointer transition-all text-center capitalize border border-transparent",
                                    priority === p
                                        ? cn(
                                              priorityColors[p],
                                              "border-transparent shadow-sm",
                                          )
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                                )}
                            >
                                {p}
                            </button>
                        ),
                    )}
                </div>
            </div>

            {annotatedScreenshot || currentScreenshot ? (
                <div className="relative rounded-xl overflow-hidden shadow-md mb-5 group">
                    <img
                        src={annotatedScreenshot || currentScreenshot || ""}
                        alt="Screenshot"
                        className="w-full block"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-3 right-3 flex gap-2">
                        <button
                            type="button"
                            onClick={handleEditScreenshot}
                            className="bg-white text-gray-700 border-none px-3 py-1.5 rounded-lg cursor-pointer text-xs! font-medium flex items-center gap-1.5 hover:bg-gray-100 shadow-lg transition-all"
                        >
                            <EditIcon className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                            type="button"
                            onClick={handleRemoveScreenshot}
                            className="bg-white text-red-600 border-none px-3 py-1.5 rounded-lg cursor-pointer text-xs! font-medium flex items-center gap-1.5 hover:bg-red-50 shadow-lg transition-all"
                        >
                            <TrashIcon className="w-3.5 h-3.5" /> Remove
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex gap-3 mb-5">
                    <button type="button"
                        onClick={handleCaptureScreenshot}
                        className="flex-1 p-4 bg-white border-2 border-dashed border-gray-300 rounded-xl cursor-pointer flex items-center justify-center gap-2.5 text-gray-600 text-sm! font-medium hover:border-matter-accent/60 hover:text-matter-accent hover:bg-matter-accent/5 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isCapturing ? (
                            <>
                                <span className="w-5 h-5 border-2 border-matter-accent/25 border-t-matter-accent rounded-full animate-spin" />
                                Capturing...
                            </>
                        ) : (
                            <>
                                <CameraIcon className="w-5 h-5" /> Screenshot
                            </>
                        )}
                    </button>

                    {isRecording ? (
                        <div className="flex-1 flex gap-2">
                            <button type="button"
                                onClick={handleStopRecording}
                                className="flex-1 p-4 bg-red-50 border-2 border-red-300 rounded-xl cursor-pointer flex items-center justify-center gap-2.5 text-red-600 text-sm! font-medium hover:bg-red-100 transition-all"
                            >
                                <span className="w-2.5 h-2.5 bg-red-600 rounded-sm animate-pulse" />
                                Stop
                            </button>
                            <button type="button"
                                onClick={handleCancelRecording}
                                className="px-4 p-4 bg-white border-2 border-gray-200 rounded-xl cursor-pointer flex items-center justify-center text-gray-500 text-sm! font-medium hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : isProcessingVideo ? (
                        <div className="flex-1 p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-2.5 text-gray-500 text-sm! font-medium">
                            <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            Processing video…
                        </div>
                    ) : (
                        <button type="button"
                            onClick={handleStartRecording}
                            className="flex-1 p-4 bg-white border-2 border-dashed border-gray-300 rounded-xl cursor-pointer flex items-center justify-center gap-2.5 text-gray-600 text-sm! font-medium hover:border-red-400 hover:text-red-600 hover:bg-red-50 hover:shadow-md transition-all"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                <circle cx="12" cy="12" r="4" fill="currentColor" />
                            </svg>
                            Record Screen
                        </button>
                    )}
                </div>
            )}

            {captureError && (
                <p role="alert" className="-mt-3 mb-5 text-xs! text-amber-700">
                    {captureError}
                </p>
            )}

            {/* Pin Placement */}
            <div className="mb-5">
                <label className="block text-sm! font-semibold mb-2 text-gray-800">
                    Pin Location{" "}
                    {!pinPosition && !initialData?.pinLocation && <span className="text-red-500">*</span>}
                </label>
                {pinError && (
                    <p className="text-xs! text-red-600 mb-2">Click to place a pin on the page before submitting.</p>
                )}
                {pinPosition ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                            <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <div className="text-sm! font-medium text-gray-800">
                                Pin placed on page
                            </div>
                            <div className="text-xs! text-gray-600">
                                Position: ({Math.round(pinPosition.pageX)},{" "}
                                {Math.round(pinPosition.pageY)})
                            </div>
                        </div>
                        <button type="button"
                            onClick={() => setIsPlacingPin(true)}
                            className="px-3 py-1.5 text-xs! font-medium text-matter-accent hover:text-matter-ink hover:bg-matter-accent/5 rounded-lg transition-all"
                        >
                            Re-place
                        </button>
                    </div>
                ) : (
                    <button type="button"
                        onClick={() => setIsPlacingPin(true)}
                        className="p-4 bg-white border-2 border-dashed border-orange-300 rounded-xl cursor-pointer flex items-center justify-center gap-2.5 text-orange-600 text-sm! font-medium hover:border-orange-400 hover:bg-orange-50 hover:shadow-md transition-all"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                        Click to place pin on page
                    </button>
                )}
            </div>

            {recordedVideo && (
                <div className="mb-5">
                    <div className="relative rounded-xl overflow-hidden shadow-md border border-gray-200">
                        <video src={recordedVideo} controls className="w-full h-auto" />
                        <button
                            type="button"
                            onClick={handleDeleteRecording}
                            className="absolute top-2 right-2 bg-white text-red-500 p-1.5 rounded-lg shadow hover:bg-gray-100 cursor-pointer border-none"
                        >
                            <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    {recordedBlobSizeMB > 50 && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs! text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                            Large video ({recordedBlobSizeMB.toFixed(0)}MB) — consider a shorter clip to avoid upload timeouts
                        </div>
                    )}
                </div>
            )}

            <div className="mb-5">
                <label className="block text-sm! font-semibold mb-2 text-gray-800">
                    Design Mockup
                </label>
                {designMockup ? (
                    <div className="relative rounded-xl overflow-hidden shadow-md group border border-gray-200">
                        <img
                            src={designMockup}
                            className="w-full block"
                            alt="Mockup"
                        />
                        <button
                            type="button"
                            onClick={() => setDesignMockup(undefined)}
                            className="absolute top-2 right-2 bg-white text-red-500 p-1.5 rounded-lg shadow hover:bg-gray-100 cursor-pointer border-none"
                        >
                            <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 bg-gray-50 flex items-center justify-center relative hover:bg-white hover:border-matter-ink/40 transition-all">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleMockupUpload}
                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        />
                        <span className="text-sm! text-gray-500 flex items-center gap-2 font-medium">
                            <PlusIcon className="w-4 h-4" /> Upload Mockup
                        </span>
                    </div>
                )}
            </div>

            {config.availableTags && config.availableTags.length > 0 && (
                <div className="mb-5">
                    <label className="block text-sm! font-semibold mb-2 text-gray-800">
                        Tags
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {config.availableTags.map((tag) => (
                            <button type="button"
                                key={tag}
                                onClick={() => toggleTag(tag)}
                                className={cn(
                                    "px-3 py-1.5 rounded-full text-xs! font-medium cursor-pointer transition-all",
                                    selectedTags.includes(tag)
                                        ? "bg-matter-ink text-matter-cream shadow-md shadow-matter-ink/30"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm",
                                )}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {submitError && (
                <div
                    role="alert"
                    className="mt-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm!"
                >
                    {submitError}
                </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6 pt-6 border-t border-gray-100">
                <button type="button"
                    onClick={onCancel}
                    className="flex-1 py-3 px-5 rounded-xl text-sm! font-semibold cursor-pointer transition-all bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md text-center"
                >
                    {config.labels?.cancelButton || "Cancel"}
                </button>
                <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    // Colours as classes, not inline: the widget's button
                    // reset (`background-color: transparent`, made !important
                    // by the build) beats an inline background, which left
                    // white text on nothing.
                    className={cn(
                        "flex-1 py-3 px-5 rounded-xl text-sm! font-semibold transition-all border-none text-center",
                        !canSubmit || isSubmitting
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-matter-ink text-matter-cream cursor-pointer hover:bg-matter-ink/85 hover:shadow-lg",
                    )}
                >
                    {isSubmitting ? (
                        recordedBlob ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Uploading video...</span>
                            </span>
                        ) : (
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto block" />
                        )
                    ) : (
                        // The configured label is for raising a ticket; the
                        // store's default filled it in on edits too.
                        initialData
                            ? "Save changes"
                            : config.labels?.submitButton || "Submit Feedback"
                    )}
                </button>
            </div>
        </form>
    );
};

export default FeedbackForm;
